import React, { useState } from 'react';
import {
  Compass, Zap, AlertTriangle, ShieldCheck, Heart, Clock,
  CheckCircle2, AlertOctagon, HelpCircle, Eye, Activity, Sparkles,
  BarChart3, ChevronRight, RefreshCw
} from 'lucide-react';
import InfoTooltip from './InfoTooltip';
import WhatIfSimulatorModal from './WhatIfSimulatorModal';

/**
 * DecisionDashboard (Athlete Decision Engine 2.0 - NousMetrics v2.5 / v3.0)
 * 
 * Responde de un vistazo a las 4 preguntas esenciales del deportista:
 * 1. ¿Estoy preparado? (Estado canónico determinista + Readiness + Fiabilidad)
 * 2. ¿Qué debo hacer? (Dosis diaria, techo de intensidad, zonas permitidas vs vetadas)
 * 3. ¿Por qué? (Drivers biológicos y de carga)
 * 4. ¿Qué debo vigilar? (Factores de precaución)
 * 
 * 100% Compatible con iOS WebKit / Safari (sin mix-blend-mode).
 */
export default function DecisionDashboard({
  decisionState,
  readinessData,
  clinicalProfile = {},
  onRefresh,
  loading = false,
  className = ''
}) {
  const [simulatorOpen, setSimulatorOpen] = useState(false);

  const state = decisionState?.state || 'TRAINABLE';
  const confidence = decisionState?.confidence || 85;
  const recommendation = decisionState?.recommendation || {};
  const why = decisionState?.why || [];
  const watch = decisionState?.watch || [];
  const inputs = decisionState?.inputs || {};

  const stateConfigs = {
    READY: {
      color: '#10b981',
      bg: 'rgba(16, 185, 129, 0.12)',
      border: 'rgba(16, 185, 129, 0.35)',
      label: 'Preparación Óptima',
      subtitle: 'Biología asimilada. Se permiten adaptaciones de alta intensidad.',
      Icon: Zap
    },
    TRAINABLE: {
      color: '#38bdf8',
      bg: 'rgba(56, 189, 248, 0.12)',
      border: 'rgba(56, 189, 248, 0.35)',
      label: 'Entrenable',
      subtitle: 'Estado favorable para carga aeróbica estándar (Z2-Z3).',
      Icon: Activity
    },
    REDUCED: {
      color: '#f59e0b',
      bg: 'rgba(245, 158, 11, 0.12)',
      border: 'rgba(245, 158, 11, 0.35)',
      label: 'Disponibilidad Modulada',
      subtitle: 'Fatiga de volumen o déficit de sueño; vetadas series lácticas.',
      Icon: AlertTriangle
    },
    RECOVERY: {
      color: '#a78bfa',
      bg: 'rgba(167, 139, 250, 0.12)',
      border: 'rgba(167, 139, 250, 0.35)',
      label: 'Recuperación Activa',
      subtitle: 'Discordancia biológica o modo lesión; sesión regenerativa suave Z1.',
      Icon: Heart
    },
    ALERT: {
      color: '#ef4444',
      bg: 'rgba(239, 68, 68, 0.12)',
      border: 'rgba(239, 68, 68, 0.35)',
      label: 'Alerta Fisiológica',
      subtitle: 'Anomalía autonómica o lectura de sensor crítica. Reposo total preventivo.',
      Icon: AlertOctagon
    },
    COMPLETED: {
      color: '#10b981',
      bg: 'rgba(16, 185, 129, 0.12)',
      border: 'rgba(16, 185, 129, 0.35)',
      label: 'Sesión Completada',
      subtitle: 'Entrenamiento de hoy finalizado. Enfoque en asimilación y descanso.',
      Icon: CheckCircle2
    }
  };

  const currentConfig = stateConfigs[state] || stateConfigs.TRAINABLE;
  const { Icon } = currentConfig;

  const allowedZones = recommendation.allowedZones || ['Z1', 'Z2', 'Z3'];
  const bannedZones = recommendation.bannedZones || ['Z5'];

  return (
    <div
      className={`glass-panel ${className}`}
      style={{
        borderRadius: '18px',
        padding: '1.4rem',
        background: 'rgba(15, 23, 42, 0.85)',
        border: `1px solid ${currentConfig.border}`,
        boxSizing: 'border-box'
      }}
    >
      {/* 1. Header con Estado Canónico */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
          <div style={{
            width: '42px',
            height: '42px',
            borderRadius: '12px',
            background: currentConfig.bg,
            border: `1px solid ${currentConfig.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Icon size={22} color={currentConfig.color} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#f8fafc' }}>
                {currentConfig.label}
              </span>
              <span style={{
                fontSize: '0.7rem',
                fontWeight: 800,
                color: currentConfig.color,
                background: currentConfig.bg,
                border: `1px solid ${currentConfig.border}`,
                borderRadius: '6px',
                padding: '2px 8px',
                textTransform: 'uppercase'
              }}>
                {state}
              </span>
            </div>
            <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)', marginTop: '2px' }}>
              {currentConfig.subtitle}
            </div>
          </div>
        </div>

        {/* Botón Simulador What-If */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <button
            onClick={() => setSimulatorOpen(true)}
            style={{
              background: 'rgba(56, 189, 248, 0.12)',
              border: '1px solid rgba(56, 189, 248, 0.3)',
              color: '#38bdf8',
              borderRadius: '10px',
              padding: '0.45rem 0.85rem',
              fontSize: '0.8rem',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer'
            }}
          >
            <BarChart3 size={15} />
            Simulador What-If
          </button>

          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={loading}
              title="Recalcular Estado"
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'rgba(255,255,255,0.7)',
                borderRadius: '10px',
                padding: '0.45rem',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
          )}
        </div>
      </div>

      {/* Grid Bento de las 4 Preguntas Clave */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
        
        {/* Pregunta 1: ¿Estoy preparado? */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '12px',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.65rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.76rem', fontWeight: 800, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              1. ¿Estoy Preparado?
            </span>
            <span style={{ fontSize: '0.72rem', color: confidence >= 80 ? '#10b981' : '#f59e0b', fontWeight: 700 }}>
              {confidence}% Confianza
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
            <span style={{ fontSize: '2rem', fontWeight: 900, color: currentConfig.color }}>
              {inputs.readiness != null ? inputs.readiness : (readinessData?.readinessScore ?? 75)}
            </span>
            <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>
              / 100 Readiness
            </span>
          </div>

          <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.35 }}>
            {inputs.tsb != null && (
              <span>Balance Coggan TSB: <strong style={{ color: inputs.tsb >= 0 ? '#10b981' : '#f59e0b' }}>{inputs.tsb > 0 ? `+${inputs.tsb}` : inputs.tsb}</strong>. </span>
            )}
            {inputs.mechanicalExposureLevel && (
              <span>Exposición Mecánica: <strong>{inputs.mechanicalExposureLevel}</strong>.</span>
            )}
          </div>
        </div>

        {/* Pregunta 2: ¿Qué debo hacer? */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '12px',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.65rem'
        }}>
          <div style={{ fontSize: '0.76rem', fontWeight: 800, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            2. ¿Qué Debo Hacer?
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '1.05rem', fontWeight: 800, color: '#f8fafc' }}>
              {recommendation.category || 'SESIÓN ESTÁNDAR'}
            </span>
            <span style={{ fontSize: '0.8rem', color: '#38bdf8', fontWeight: 700 }}>
              {recommendation.targetDuration || '45 - 60 min'}
            </span>
          </div>

          {/* Zonas Permitidas vs Vetadas */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>Zonas:</span>
            {allowedZones.map(z => (
              <span key={z} style={{
                fontSize: '0.68rem',
                fontWeight: 800,
                background: 'rgba(16, 185, 129, 0.15)',
                color: '#10b981',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                padding: '1px 6px',
                borderRadius: '4px'
              }}>
                {z}
              </span>
            ))}
            {bannedZones.map(z => (
              <span key={z} style={{
                fontSize: '0.68rem',
                fontWeight: 800,
                background: 'rgba(239, 68, 68, 0.12)',
                color: '#ef4444',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                textDecoration: 'line-through',
                padding: '1px 6px',
                borderRadius: '4px'
              }}>
                {z}
              </span>
            ))}
          </div>

          <div style={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.3 }}>
            {recommendation.targetZones || 'Mantén el esfuerzo por debajo de VT1.'}
          </div>
        </div>

        {/* Pregunta 3: ¿Por qué? */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '12px',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}>
          <div style={{ fontSize: '0.76rem', fontWeight: 800, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            3. ¿Por Qué? (Fundamento)
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {why.slice(0, 3).map((reason, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '0.76rem', color: 'rgba(255,255,255,0.85)' }}>
                <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#38bdf8', marginTop: '6px', flexShrink: 0 }} />
                <span>{reason}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Pregunta 4: ¿Qué debo vigilar? */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.06)',
          borderRadius: '12px',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}>
          <div style={{ fontSize: '0.76rem', fontWeight: 800, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Eye size={13} color="#f59e0b" />
            4. ¿Qué Debo Vigilar?
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {watch.slice(0, 3).map((item, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '0.76rem', color: 'rgba(255,255,255,0.85)' }}>
                <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#f59e0b', marginTop: '6px', flexShrink: 0 }} />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Modal Simulador What-If */}
      {simulatorOpen && (
        <WhatIfSimulatorModal
          isOpen={simulatorOpen}
          onClose={() => setSimulatorOpen(false)}
          currentCtl={inputs.ctl != null ? Number(inputs.ctl) : (readinessData?.ctl != null ? Number(readinessData.ctl) : 0)}
          currentAtl={inputs.atl != null ? Number(inputs.atl) : (readinessData?.atl != null ? Number(readinessData.atl) : 0)}
          currentTsb={inputs.tsb != null ? Number(inputs.tsb) : (readinessData?.tsb != null ? Number(readinessData.tsb) : 0)}
          currentReadiness={inputs.readiness || readinessData?.readinessScore || 75}
        />
      )}
    </div>
  );
}
